<script>
  // GeneratorPanel — per-tab pipeline runner with full config controls.
  //
  // SSE lifetime: the EventSource lives on `tab.eventSource` in the store,
  // NOT on this component. So switching workspace tabs / panels does NOT
  // close the SSE — the running job keeps streaming updates into tab.job
  // even when this component is unmounted. On remount, $effect re-attaches
  // the elapsed-timer ticker; SSE state is already in tab.job from earlier
  // ticks. Results render progressively as files arrive.
  import { onDestroy, onMount } from "svelte";
  import { jobsApi, api } from "../api/client.js";
  import { saveNewTabDefaults, openJobSse, closeJobSse } from "../stores/app.svelte.js";

  let { tab } = $props();

  // Runtime-only state (not config) — fine to reset on remount.
  let dragOver = $state(false);

  // Elapsed timer: tab.startedAt is captured at submit and survives
  // component remounts. `now` is a local ticker that re-derives every
  // 100ms — even after switching tabs, the display jumps right back
  // to (now - tab.startedAt) which IS the correct wall-clock elapsed.
  let now = $state(Date.now() / 1000);
  $effect(() => {
    if (tab.view !== "tracking" && tab.view !== "submitting") return;
    const iv = setInterval(() => { now = Date.now() / 1000; }, 100);
    return () => clearInterval(iv);
  });
  const elapsedDisplay = $derived(
    tab.startedAt ? `${(now - tab.startedAt).toFixed(1)}s` : "0.0s"
  );

  // Generation model preset / custom mode: dropdown if model is one of
  // the 2 presets, free-text otherwise. forceCustom flips to text input
  // when user picks "Custom..." in the dropdown.
  const GEN_MODEL_PRESETS = ["gpt-image-1-all", "gpt-image-2-all"];
  let forceCustom = $state(false);
  const genModelCustom = $derived(forceCustom || !GEN_MODEL_PRESETS.includes(tab.generationModel));
  function onGenModelSelect(e) {
    const v = e.target.value;
    if (v === "__custom") {
      forceCustom = true;
    } else {
      forceCustom = false;
      tab.generationModel = v;
    }
  }

  // Pipeline → mode mapping for the form's `mode` field.
  const TAB_TO_MODE = {
    themepack: "generate",
    control_center: "control_center",
    keyboard: "keyboard",
    cc_preview: "cc_preview",
  };

  // --- CC preview: secondary character file handling --------------------
  // Wallpaper uses the shared tab.files dropzone; character is a second
  // uploader that only appears when pipelineTab === "cc_preview".
  function handleCharacterFile(files) {
    if (!files || files.length === 0) return;
    for (const u of tab.ccPreviewCharacterPreviews) URL.revokeObjectURL(u);
    tab.ccPreviewCharacter = Array.from(files).slice(0, 1);
    tab.ccPreviewCharacterPreviews = tab.ccPreviewCharacter.map((f) => URL.createObjectURL(f));
  }
  function clearCharacterFile() {
    for (const u of tab.ccPreviewCharacterPreviews) URL.revokeObjectURL(u);
    tab.ccPreviewCharacter = [];
    tab.ccPreviewCharacterPreviews = [];
  }
  function onCharacterFileInput(e) { handleCharacterFile(e.target.files); }
  function onCharacterDrop(e) {
    e.preventDefault();
    dragOverCharacter = false;
    handleCharacterFile(e.dataTransfer.files);
  }
  let dragOverCharacter = $state(false);

  // Stage-toggle handler with cascade rules:
  //   • Themepack: sheet 2 needs sheet 1 as a style anchor. Tắt sheet 1 →
  //     auto-tắt sheet 2; bật sheet 2 trong khi sheet 1 đang tắt →
  //     auto-bật luôn sheet 1.
  // Each pipeline can grow its own dependency rules here without
  // touching the template loop.
  function onStageToggle(key, value) {
    const pipe = tab.pipelineTab;
    const stages = tab.stages[pipe];
    if (!stages) return;
    stages[key] = value;
    if (pipe === "themepack") {
      if (key === "gen_icons_sheet_1" && value === false) {
        stages.gen_icons_sheet_2 = false;
      } else if (key === "gen_icons_sheet_2" && value === true) {
        stages.gen_icons_sheet_1 = true;
      }
    }
  }

  // Save current config as the default for NEW tabs the user opens later.
  $effect(() => {
    saveNewTabDefaults({
      pipelineTab:      tab.pipelineTab,
      detectionBackend: tab.detectionBackend,
      detectionModel:   tab.detectionModel,
      inpaintModel:     tab.inpaintModel,
      generationModel:  tab.generationModel,
      genStrategy:      tab.genStrategy,
      chatProvider:     tab.chatProvider,
      imageEndpoint:    tab.imageEndpoint,
      stages:           tab.stages,
      padding:          tab.padding,
      // CC preview text presets (files intentionally NOT persisted)
      ccPreviewLabelText:  tab.ccPreviewLabelText,
      ccPreviewTrackTitle: tab.ccPreviewTrackTitle,
      ccPreviewArtistName: tab.ccPreviewArtistName,
    });
  });

  // On mount: if this tab has a running job but no live SSE (e.g. user
  // opened it from JobsModal, or page reloaded mid-job), reattach.
  onMount(() => {
    if (tab.jobId && (tab.view === "tracking" || tab.view === "submitting") && !tab.eventSource) {
      openJobSse(tab, tab.jobId);
    }
  });

  // --- File handling ----------------------------------------------------
  function handleFile(files) {
    if (!files || files.length === 0) return;
    for (const u of tab.filePreviews) URL.revokeObjectURL(u);
    tab.files = Array.from(files).slice(0, 1);
    tab.filePreviews = tab.files.map((f) => URL.createObjectURL(f));
  }
  function clearFiles() {
    for (const u of tab.filePreviews) URL.revokeObjectURL(u);
    tab.files = [];
    tab.filePreviews = [];
  }
  function onFileInput(e) { handleFile(e.target.files); }
  function onDrop(e) {
    e.preventDefault();
    dragOver = false;
    handleFile(e.dataTransfer.files);
  }

  // --- Submit -----------------------------------------------------------
  async function submit() {
    if (tab.files.length === 0) {
      tab.error = "Chưa có ảnh upload.";
      tab.view = "failed";
      return;
    }
    if (tab.view === "submitting" || tab.view === "tracking") return;

    tab.view = "submitting";
    tab.job = null;
    tab.error = null;
    tab.startedAt = Date.now() / 1000;
    closeJobSse(tab);

    const fd = new FormData();
    fd.append("image", tab.files[0]);
    fd.append("padding", String(tab.padding));
    fd.append("mode", TAB_TO_MODE[tab.pipelineTab] || "generate");
    if (!tab.isDefault) fd.append("key_id", tab.keyId);
    fd.append("detection_backend", tab.detectionBackend);
    fd.append("detection_model", tab.detectionModel);
    fd.append("inpaint_model", tab.inpaintModel);
    fd.append("generation_model", tab.generationModel);
    fd.append("gen_strategy", tab.genStrategy);
    fd.append("chat_provider", tab.chatProvider);
    if (tab.chatProvider === "openai" && tab.chatPassword) {
      fd.append("chat_password", tab.chatPassword);
    }
    fd.append("image_endpoint", tab.imageEndpoint);
    for (const [key, val] of Object.entries(tab.stages[tab.pipelineTab] || {})) {
      fd.append(key, val ? "true" : "false");
    }
    // CC preview mode: attach the character upload + 3 text placeholders.
    // Server ignores these fields when mode !== "cc_preview".
    if (tab.pipelineTab === "cc_preview") {
      if (tab.ccPreviewCharacter[0]) {
        fd.append("character_image", tab.ccPreviewCharacter[0]);
      }
      // Empty strings intentionally sent through — backend detects and
      // switches those slots to "auto" mode (model picks a themed word).
      fd.append("label_text",  tab.ccPreviewLabelText  ?? "");
      fd.append("track_title", tab.ccPreviewTrackTitle ?? "");
      fd.append("artist_name", tab.ccPreviewArtistName ?? "");
    }
    fd.append("pipeline_version", "1");

    try {
      const { job_id } = await jobsApi.submit(fd);
      tab.jobId = job_id;
      tab.view = "tracking";
      openJobSse(tab, job_id);
      // Reflect the new job in the URL so the user can share / refresh.
      _writeJobHash(job_id);
    } catch (err) {
      tab.view = "failed";
      tab.error = err.message;
    }
  }

  // Lightweight hash router — `#/job/<id>` makes the URL share/refresh-able.
  // The hash is read on app boot (see App.svelte) to auto-load the job.
  function _writeJobHash(jobId) {
    if (typeof window === "undefined" || !jobId) return;
    const next = `#/job/${jobId}`;
    if (window.location.hash !== next) {
      try { window.history.replaceState(null, "", next); } catch { /* */ }
    }
  }

  async function cancelJob() {
    if (!tab.jobId) return;
    try { await jobsApi.cancel(tab.jobId); }
    catch (err) { console.warn("Cancel failed:", err.message); }
  }

  async function continueJob() {
    if (!tab.jobId) return;
    try {
      const body = tab.chatPassword ? { chat_password: tab.chatPassword } : {};
      await api.post(`/api/jobs/${tab.jobId}/continue`, body);
      tab.view = "tracking";
      tab.error = null;
      tab.startedAt = Date.now() / 1000;
      openJobSse(tab, tab.jobId);
    } catch (err) {
      tab.error = `Tiếp tục thất bại: ${err.message}`;
    }
  }

  // NOTE: do NOT close SSE on component unmount. SSE must persist while
  // a job is running so the user can switch tabs/panels and come back
  // to see updates. SSE is cleaned up only when:
  //   • SSE itself fires "done" (job ended)
  //   • User explicitly submits a new job (which closes the old one)
  //   • Tab is closed in the store (closeTab() in app.svelte.js)
  onDestroy(() => { /* intentionally empty */ });

  // --- Derived helpers --------------------------------------------------
  const stage = $derived(tab.job?.stage || "idle");
  const isRunning = $derived(tab.view === "submitting" || tab.view === "tracking");
  const canContinue = $derived(!!tab.job?.can_continue && (tab.view === "failed" || tab.view === "cancelled"));

  // Show the results panel as soon as ANY artifact arrives — don't wait
  // for the whole pipeline to succeed. Progressive feedback while running.
  // `input` is the source image URL (not an artifact) — excluded so an
  // in-progress job with only the input recorded doesn't pre-show the
  // results panel with 0 actual artifacts.
  const NON_ARTIFACT_KEYS = new Set(["crops", "input"]);
  const hasAnyResults = $derived(() => {
    const f = tab.job?.files;
    if (!f) return false;
    if (f.crops?.length > 0) return true;
    for (const k of Object.keys(f)) {
      if (NON_ARTIFACT_KEYS.has(k)) continue;
      if (f[k]) return true;
    }
    return false;
  });
  // Input image fallback: when the tab has no locally-uploaded File (e.g.
  // user opened the job from history), use the server-stored input URL
  // so the dropzone shows the original screenshot instead of an empty
  // "drop here" placeholder.
  const inputImageUrl = $derived(
    tab.filePreviews[0] || tab.job?.files?.input || null
  );
  const inputImageName = $derived(
    tab.files[0]?.name || (tab.job?.files?.input ? "input.png (from history)" : null)
  );

  // Filename inferred from URL for the Download button's `download` attr.
  function fileNameFromUrl(url) {
    if (!url) return "download";
    try {
      return new URL(url, window.location.origin).pathname.split("/").pop() || "download";
    } catch { return "download"; }
  }
</script>

<div class="panel">
  <!-- 1) Pipeline mode tabs -->
  <div class="pipeline-tabs card">
    <button class:active={tab.pipelineTab === "themepack"}
            onclick={() => (tab.pipelineTab = "themepack")} disabled={isRunning}>
      🎨 Themepack
    </button>
    <button class:active={tab.pipelineTab === "control_center"}
            onclick={() => (tab.pipelineTab = "control_center")} disabled={isRunning}>
      🎛️ Control Center
    </button>
    <button class:active={tab.pipelineTab === "keyboard"}
            onclick={() => (tab.pipelineTab = "keyboard")} disabled={isRunning}>
      ⌨️ Keyboard
    </button>
    <button class:active={tab.pipelineTab === "cc_preview"}
            onclick={() => (tab.pipelineTab = "cc_preview")} disabled={isRunning}>
      🎬 CC Preview
    </button>
  </div>

  <!-- 2) Upload -->
  <div class="dropzone card" class:dragging={dragOver}
       ondragover={(e) => { e.preventDefault(); dragOver = true; }}
       ondragleave={() => (dragOver = false)}
       ondrop={onDrop}
       role="button" tabindex="0">
    <input type="file" id="file-{tab.keyId}" hidden
           accept="image/png,image/jpeg,image/webp" onchange={onFileInput} />

    {#if inputImageUrl}
      <div class="preview">
        <img src={inputImageUrl} alt="" />
        <div class="preview-meta">
          <span>{inputImageName}</span>
          {#if tab.files.length > 0}
            <button class="link-btn" onclick={clearFiles} disabled={isRunning}>Đổi ảnh</button>
          {:else}
            <label for="file-{tab.keyId}" class="link-btn">Upload ảnh khác</label>
          {/if}
        </div>
      </div>
    {:else}
      <label for="file-{tab.keyId}" class="dropzone-empty">
        <span class="upload-icon">📥</span>
        <strong>
          {#if tab.pipelineTab === "cc_preview"}
            Click hoặc kéo-thả ảnh WALLPAPER (nền)
          {:else}
            Click hoặc kéo-thả ảnh style mẫu vào đây
          {/if}
        </strong>
        <span class="muted">PNG / JPG / WEBP</span>
      </label>
    {/if}
  </div>

  <!-- 2b) CC preview extras: secondary character dropzone + text inputs -->
  {#if tab.pipelineTab === "cc_preview"}
    <div class="dropzone card" class:dragging={dragOverCharacter}
         ondragover={(e) => { e.preventDefault(); dragOverCharacter = true; }}
         ondragleave={() => (dragOverCharacter = false)}
         ondrop={onCharacterDrop}
         role="button" tabindex="0">
      <input type="file" id="char-file-{tab.keyId}" hidden
             accept="image/png,image/jpeg,image/webp" onchange={onCharacterFileInput} />
      {#if tab.ccPreviewCharacterPreviews[0]}
        <div class="preview">
          <img src={tab.ccPreviewCharacterPreviews[0]} alt="" />
          <div class="preview-meta">
            <span>{tab.ccPreviewCharacter[0]?.name || "character.png"}</span>
            <button class="link-btn" onclick={clearCharacterFile} disabled={isRunning}>Đổi ảnh</button>
          </div>
        </div>
      {:else}
        <label for="char-file-{tab.keyId}" class="dropzone-empty">
          <span class="upload-icon">🐰</span>
          <strong>Click hoặc kéo-thả ảnh CHARACTER (nhân vật cho media widget)</strong>
          <span class="muted">
            Optional — nếu bỏ trống, model tự sinh nhân vật cho media widget
          </span>
        </label>
      {/if}
    </div>

    <div class="cc-preview-fields card">
      <div class="stages-header">Text placeholders — bỏ trống = model tự chọn theo mood wallpaper</div>
      <div class="cc-preview-grid">
        <label class="full">
          <span class="muted">Decoration hint (optional) — 4 mini-vignettes cho row giữa</span>
          <input type="text" maxlength="120"
                 bind:value={tab.ccPreviewLabelText}
                 placeholder="Auto — vd: 'các loài hoa close-up', 'chân dung anime dark tone', 'pottery detail shots'"
                 disabled={isRunning} />
        </label>
        <label>
          <span class="muted">Track title</span>
          <input type="text" maxlength="40"
                 bind:value={tab.ccPreviewTrackTitle}
                 placeholder="Auto (model chọn theo theme)" disabled={isRunning} />
        </label>
        <label>
          <span class="muted">Artist name</span>
          <input type="text" maxlength="40"
                 bind:value={tab.ccPreviewArtistName}
                 placeholder="Auto (model chọn theo theme)" disabled={isRunning} />
        </label>
      </div>
      <p class="stages-hint muted">
        <strong>Decoration hint</strong>: mô tả ngắn 4 mini-images ở row giữa (thay
        thế row chữ JUDY cũ). Bỏ trống → model tự chọn 4 vignette matching
        wallpaper (vd. wallpaper hoa → 4 loại hoa khác nhau; wallpaper anime →
        4 chân dung/scene khác nhau).
      </p>
    </div>
  {/if}

  <!-- 3) Stage toggles per pipeline (hidden for cc_preview — single-shot) -->
  {#if tab.pipelineTab !== "cc_preview"}
    <div class="stages card">
      <div class="stages-header">Stages — bật/tắt từng phần</div>
      <div class="stages-grid">
        {#each Object.entries(tab.stages[tab.pipelineTab] || {}) as [key, val]}
          <label class="stage-toggle">
            <input type="checkbox" checked={val} disabled={isRunning}
                   onchange={(e) => onStageToggle(key, e.target.checked)} />
            <span>{key}</span>
          </label>
        {/each}
      </div>
      <p class="stages-hint muted">
        Tắt = skip + reuse artifact từ disk nếu có.
        {#if tab.pipelineTab === "themepack"}
          <strong class="hint-warn">Lưu ý:</strong> sheet 2 cần sheet 1 làm anchor — tắt sheet 1 sẽ tự tắt sheet 2.
        {/if}
      </p>
    </div>
  {/if}

  <!-- 4) Model selectors -->
  <details class="config card">
    <summary>⚙️ Models &amp; settings</summary>
    <div class="config-grid">
      <label>
        <span class="muted">Detection backend</span>
        <select bind:value={tab.detectionBackend} disabled={isRunning}>
          <option value="api">API (ShopAIKey relay)</option>
          <option value="browser">Browser (Playwright)</option>
        </select>
      </label>
      <label>
        <span class="muted">Detection model</span>
        <input type="text" bind:value={tab.detectionModel} disabled={isRunning} list="model-list" />
      </label>
      <label>
        <span class="muted">Inpaint model</span>
        <input type="text" bind:value={tab.inpaintModel} disabled={isRunning} list="model-list" />
      </label>
      <label>
        <span class="muted">Generation model</span>
        <select value={genModelCustom ? "__custom" : tab.generationModel}
                onchange={onGenModelSelect} disabled={isRunning}>
          <option value="gpt-image-1-all">gpt-image-1-all (default)</option>
          <option value="gpt-image-2-all">gpt-image-2-all</option>
          <option value="__custom">Custom...</option>
        </select>
        {#if genModelCustom}
          <input type="text" bind:value={tab.generationModel}
                 placeholder="vd: gemini-2.5-flash-image, qwen-image-edit-2509"
                 disabled={isRunning} list="model-list" />
        {/if}
      </label>
      <label>
        <span class="muted">Gen strategy</span>
        <select bind:value={tab.genStrategy} disabled={isRunning}>
          <option value="sequential">Sequential</option>
          <option value="parallel">Parallel (≈4× faster)</option>
        </select>
      </label>
      <label>
        <span class="muted">Chat provider</span>
        <select bind:value={tab.chatProvider} disabled={isRunning}>
          <option value="shopaikey">ShopAIKey (default)</option>
          <option value="openai">OpenAI direct (requires password)</option>
        </select>
      </label>
      <label>
        <span class="muted">Image endpoint</span>
        <select bind:value={tab.imageEndpoint} disabled={isRunning}>
          <option value="images_edits">/v1/images/edits (multipart)</option>
          <option value="chat_completions">/v1/chat/completions (legacy)</option>
        </select>
      </label>
      {#if tab.chatProvider === "openai"}
        <label class="full">
          <span class="muted">OpenAI gate password</span>
          <input type="password" bind:value={tab.chatPassword} placeholder="taolaadmin" disabled={isRunning} />
        </label>
      {/if}
    </div>
    <datalist id="model-list">
      <option value="gpt-image-1-all"></option>
      <option value="gpt-image-2-all"></option>
      <option value="gemini-2.5-flash"></option>
      <option value="gemini-2.5-flash-image"></option>
      <option value="gemini-2.5-pro"></option>
      <option value="qwen-image-edit-2509"></option>
    </datalist>
  </details>

  <!-- 5) Padding + Run + Cancel -->
  <div class="run-row card">
    <label class="padding-field">
      <span class="muted">Padding</span>
      <input type="number" min="0" max="32" bind:value={tab.padding} disabled={isRunning} />
      <span class="muted">px</span>
    </label>
    <div class="run-actions">
      {#if isRunning && tab.jobId}
        <button class="link-btn" onclick={cancelJob}>Hủy</button>
      {/if}
      <button class="primary-btn" disabled={isRunning || tab.files.length === 0} onclick={submit}>
        {#if tab.view === "submitting"}
          Đang upload... ({elapsedDisplay})
        {:else if tab.view === "tracking"}
          Đang chạy... ({elapsedDisplay})
        {:else}
          ▶ Run pipeline
        {/if}
      </button>
    </div>
  </div>

  <!-- 6) Status -->
  {#if tab.view !== "idle"}
    <div class="status card">
      <div class="status-head">
        <strong>Status:</strong>
        <span class="stage-badge stage-{stage}">{stage}</span>
        <span class="muted">{tab.job?.message || ""}</span>
        {#if isRunning}
          <span class="elapsed-pill">{elapsedDisplay}</span>
        {/if}
      </div>
      {#if tab.view === "tracking"}
        <div class="progress-bar">
          <div class="progress-fill stage-{stage}"></div>
        </div>
      {/if}
    </div>
  {/if}

  <!-- 7) Error / Cancelled card with continue + raw error -->
  {#if (tab.view === "failed" && tab.error) || (tab.view === "cancelled" && canContinue)}
    <div class="error-card card">
      <h3>{tab.view === "cancelled" ? "Đã huỷ" : "Lỗi xử lý"}</h3>
      {#if tab.error}
        <pre>{tab.error.length > 240 ? tab.error.slice(0, 240) + "…" : tab.error}</pre>
        {#if tab.error.length > 240}
          <details class="raw-error-details">
            <summary>📋 Xem raw error</summary>
            <pre class="raw-error-full">{tab.error}</pre>
          </details>
        {/if}
      {/if}
      {#if canContinue}
        <p class="continue-hint muted">
          Các kết quả đã hoàn tất được giữ lại. Tiếp tục sẽ chỉ tạo lại phần còn thiếu.
        </p>
        <button class="primary-btn" onclick={continueJob}>Tiếp tục</button>
      {/if}
    </div>
  {/if}

  <!-- 8) Results — render progressively as files arrive (not gated by succeeded) -->
  {#if hasAnyResults()}
    <div class="results">
      <div class="results-header">
        <h3 class="results-title">
          {tab.view === "succeeded" ? "✓ Kết quả" : "Đang sinh kết quả..."}
        </h3>
        <span class="muted results-count">
          {Object.keys(tab.job.files).filter(k => !NON_ARTIFACT_KEYS.has(k) && tab.job.files[k]).length} files
          {#if tab.job.files.crops?.length}+ {tab.job.files.crops.length} crops{/if}
        </span>
      </div>

      {#if tab.job.files.cc_background || tab.job.files.cc_surfaces ||
            tab.job.files.cc_pills_decor || tab.job.files.cc_toggle_icons}
        <h4 class="result-section-title">Control Center sheets</h4>
        <div class="results-grid">
          {#each ["cc_background", "cc_surfaces", "cc_pills_decor", "cc_toggle_icons"] as key}
            {#if tab.job.files[key]}
              <div class="result-card">
                <a class="result-img-link" href={tab.job.files[key]} target="_blank" rel="noopener">
                  <img src={tab.job.files[key]} alt={key} />
                </a>
                <div class="result-meta">
                  <span class="result-label">{key}</span>
                  <a class="download-btn" href={tab.job.files[key]} download={fileNameFromUrl(tab.job.files[key])} title="Download">
                    ⬇
                  </a>
                </div>
              </div>
            {/if}
          {/each}
        </div>
      {/if}

      {#if tab.job.files.cc_preview}
        <h4 class="result-section-title">CC Preview</h4>
        <div class="results-grid results-grid-large">
          <div class="result-card result-card-wide">
            <a class="result-img-link" href={tab.job.files.cc_preview} target="_blank" rel="noopener">
              <img src={tab.job.files.cc_preview} alt="cc_preview" />
            </a>
            <div class="result-meta">
              <span class="result-label">cc_preview.webp</span>
              <a class="download-btn" href={tab.job.files.cc_preview} download={fileNameFromUrl(tab.job.files.cc_preview)} title="Download">
                ⬇
              </a>
            </div>
          </div>
          {#if tab.job.files.cc_preview_character}
            <div class="result-card">
              <a class="result-img-link" href={tab.job.files.cc_preview_character} target="_blank" rel="noopener">
                <img src={tab.job.files.cc_preview_character} alt="character" />
              </a>
              <div class="result-meta">
                <span class="result-label">character (input)</span>
                <a class="download-btn" href={tab.job.files.cc_preview_character} download={fileNameFromUrl(tab.job.files.cc_preview_character)} title="Download">
                  ⬇
                </a>
              </div>
            </div>
          {/if}
        </div>
      {/if}

      {#if tab.job.files.kb_background || tab.job.files.kb_sheet}
        <h4 class="result-section-title">Keyboard</h4>
        <div class="results-grid">
          {#each ["kb_background", "kb_sheet"] as key}
            {#if tab.job.files[key]}
              <div class="result-card">
                <a class="result-img-link" href={tab.job.files[key]} target="_blank" rel="noopener">
                  <img src={tab.job.files[key]} alt={key} />
                </a>
                <div class="result-meta">
                  <span class="result-label">{key}</span>
                  <a class="download-btn" href={tab.job.files[key]} download={fileNameFromUrl(tab.job.files[key])} title="Download">
                    ⬇
                  </a>
                </div>
              </div>
            {/if}
          {/each}
        </div>
      {/if}

      {#if tab.job.files.background || tab.job.files.icons_sheet_1 || tab.job.files.icons_sheet_2 ||
            tab.job.files.icons_sheet || tab.job.files.widgets_sheet ||
            tab.job.files.annotated || tab.job.files.icons_annotated_1 || tab.job.files.icons_annotated_2 ||
            tab.job.files.mask || tab.job.files.clean_background ||
            tab.job.files.widgets_annotated}
        <h4 class="result-section-title">Themepack</h4>
        <div class="results-grid">
          <!-- Order: background → sheet 1 + its annotation → sheet 2 + its annotation →
               widgets → legacy fields. `icons_sheet` (no suffix) only renders for
               pre-split jobs that have the old merged file. -->
          {#each ["background", "icons_sheet_1", "icons_annotated_1", "icons_sheet_2", "icons_annotated_2", "widgets_sheet", "widgets_annotated", "annotated", "icons_sheet", "mask", "clean_background"] as key}
            {#if tab.job.files[key]}
              <div class="result-card">
                <a class="result-img-link" href={tab.job.files[key]} target="_blank" rel="noopener">
                  <img src={tab.job.files[key]} alt={key} />
                </a>
                <div class="result-meta">
                  <span class="result-label">{key}</span>
                  <a class="download-btn" href={tab.job.files[key]} download={fileNameFromUrl(tab.job.files[key])} title="Download">
                    ⬇
                  </a>
                </div>
              </div>
            {/if}
          {/each}
        </div>
      {/if}

      {#if tab.job.files.crops?.length > 0}
        <h4 class="result-section-title">Crops ({tab.job.files.crops.length})</h4>
        <div class="crops-grid">
          {#each tab.job.files.crops as crop}
            <a class="crop-cell" href={crop.url} target="_blank" rel="noopener" title={crop.type}>
              <img src={crop.url} alt={crop.type} loading="lazy" />
            </a>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .panel { display: flex; flex-direction: column; gap: 12px; }

  /* Pipeline mode tabs */
  .pipeline-tabs { display: flex; gap: 6px; padding: 10px; }
  .pipeline-tabs button {
    flex: 1; padding: 10px 14px; border-radius: 6px;
    font-size: 0.88rem;
    background: var(--panel-2); color: var(--muted-strong);
    border: 1px solid var(--border);
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .pipeline-tabs button:hover:not(:disabled) {
    border-color: var(--border-strong); color: var(--text);
  }
  .pipeline-tabs button.active {
    border-color: var(--accent); color: var(--text-strong);
    background: rgba(124, 158, 255, 0.08);
  }

  /* Dropzone */
  .dropzone {
    border: 2px dashed var(--border); border-radius: var(--radius);
    padding: 28px; text-align: center;
    transition: border-color 0.15s, background 0.15s; cursor: pointer;
  }
  .dropzone:hover, .dropzone.dragging {
    border-color: var(--accent); background: rgba(124, 158, 255, 0.04);
  }
  .dropzone-empty {
    display: flex; flex-direction: column; align-items: center; gap: 8px; cursor: pointer;
  }
  .upload-icon { font-size: 2.2rem; }
  .muted { color: var(--muted); font-size: 0.82rem; }
  .preview { display: flex; flex-direction: column; gap: 10px; align-items: center; }
  .preview img {
    max-height: 240px; max-width: 100%; border-radius: var(--radius-sm);
    border: 1px solid var(--border);
  }
  .preview-meta {
    display: flex; gap: 12px; align-items: center; font-size: 0.82rem; color: var(--text);
  }

  /* Stages */
  .stages { padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
  .stages-header {
    font-size: 0.72rem; text-transform: uppercase;
    letter-spacing: 0.04em; color: var(--muted);
  }
  .stages-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px;
  }
  .stage-toggle {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 6px 10px; background: var(--panel-2);
    border: 1px solid var(--border); border-radius: 6px;
    font-size: 0.82rem; cursor: pointer;
  }
  .stage-toggle input { accent-color: var(--accent); }
  .stages-hint { font-size: 0.76rem; }
  .hint-warn { color: var(--warning, #f0b429); font-weight: 600; }

  /* CC preview text fields */
  .cc-preview-fields { padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
  .cc-preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
  }
  .cc-preview-grid label {
    display: flex; flex-direction: column; gap: 4px;
    font-size: 0.82rem;
  }
  .cc-preview-grid label.full { grid-column: 1 / -1; }
  .cc-preview-grid input {
    padding: 6px 10px;
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 4px;
    font-size: 0.88rem;
  }

  /* Config (collapsible) */
  .config { padding: 12px 14px; }
  .config summary {
    cursor: pointer; font-size: 0.88rem;
    color: var(--text-strong); padding: 4px 0;
  }
  .config[open] summary { margin-bottom: 12px; }
  .config-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px;
  }
  .config-grid label {
    display: flex; flex-direction: column; gap: 4px; font-size: 0.82rem;
  }
  .config-grid label.full { grid-column: 1 / -1; }
  .config-grid input, .config-grid select { padding: 6px 10px; }

  /* Run row */
  .run-row {
    display: flex; gap: 14px; align-items: center; justify-content: space-between;
    padding: 12px 14px;
  }
  .padding-field { display: inline-flex; align-items: center; gap: 6px; font-size: 0.85rem; }
  .padding-field input { width: 60px; }
  .run-actions { display: flex; gap: 10px; align-items: center; }

  /* Status */
  .status { padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; }
  .status-head {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 0.85rem;
  }
  .stage-badge {
    padding: 2px 10px; border-radius: 999px;
    font-size: 0.72rem; font-weight: 600; text-transform: uppercase;
    background: var(--panel-2); color: var(--muted-strong);
  }
  .stage-badge.stage-done { background: rgba(74, 222, 128, 0.15); color: #4cd197; }
  .stage-badge.stage-error,
  .stage-badge.stage-interrupted {
    background: rgba(248, 113, 113, 0.15); color: #ff8585;
  }
  .stage-badge.stage-cancelled { background: rgba(138, 145, 163, 0.15); }
  .stage-badge.stage-generate,
  .stage-badge.stage-detect,
  .stage-badge.stage-crop,
  .stage-badge.stage-inpaint {
    background: rgba(124, 158, 255, 0.15); color: var(--accent);
  }
  .elapsed-pill {
    margin-left: auto;
    padding: 2px 10px;
    border-radius: 999px;
    background: var(--panel-2);
    color: var(--text);
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }
  .progress-bar { height: 4px; background: var(--panel-2); border-radius: 2px; overflow: hidden; }
  .progress-fill {
    height: 100%; width: 0; background: var(--accent);
    animation: progress 1.5s ease-in-out infinite;
  }
  @keyframes progress { 0%{width:0;} 50%{width:60%;} 100%{width:100%;} }

  /* Error */
  .error-card {
    padding: 14px 16px;
    background: rgba(248, 113, 113, 0.05);
    border-color: rgba(248, 113, 113, 0.3);
    display: flex; flex-direction: column; gap: 10px;
  }
  .error-card h3 { margin: 0; font-size: 0.9rem; color: #ff8585; }
  .error-card pre {
    margin: 0; font-size: 0.78rem; color: var(--text);
    white-space: pre-wrap; word-break: break-word;
    font-family: ui-monospace, monospace;
  }
  .raw-error-details summary { cursor: pointer; color: var(--accent); font-size: 0.82rem; }
  .raw-error-full {
    margin-top: 8px; padding: 10px; background: var(--bg);
    border-radius: 6px; max-height: 320px; overflow: auto;
  }
  .continue-hint { margin: 0; font-size: 0.78rem; }

  /* Results */
  .results { display: flex; flex-direction: column; gap: 14px; }
  .results-header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px;
  }
  .results-title { margin: 0; font-size: 1rem; color: var(--text-strong); }
  .results-count { font-size: 0.78rem; font-variant-numeric: tabular-nums; }
  .result-section-title {
    margin: 0; font-size: 0.85rem; color: var(--muted-strong);
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .results-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px;
  }
  /* CC preview grid — main mockup gets a wider slot so the full 1170×2532
     result is legible without opening in a new tab. */
  .results-grid-large {
    grid-template-columns: minmax(280px, 1fr) minmax(180px, 240px);
  }
  .result-card-wide img { aspect-ratio: 804 / 1748; }
  @media (max-width: 720px) {
    .results-grid-large { grid-template-columns: 1fr; }
  }
  .result-card {
    display: flex; flex-direction: column;
    background: var(--panel); border: 1px solid var(--border);
    border-radius: var(--radius-sm); overflow: hidden;
    transition: border-color 0.15s, transform 0.1s;
  }
  .result-card:hover { border-color: var(--accent); transform: translateY(-1px); }
  .result-img-link { display: block; }
  .result-card img {
    width: 100%; aspect-ratio: 4/3; object-fit: contain;
    background: #0d0f16; display: block;
  }
  .result-meta {
    display: flex; align-items: center; justify-content: space-between;
    padding: 6px 10px; border-top: 1px solid var(--border);
    gap: 8px;
  }
  .result-label {
    font-size: 0.74rem; color: var(--muted-strong);
    font-family: ui-monospace, monospace;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .download-btn {
    flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    width: 24px; height: 24px;
    background: var(--panel-2); border: 1px solid var(--border);
    border-radius: 4px;
    color: var(--text); text-decoration: none;
    font-size: 0.85rem;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
  }
  .download-btn:hover {
    background: var(--accent); border-color: var(--accent);
    color: var(--bg);
  }

  /* Crops dense grid */
  .crops-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 6px;
  }
  .crop-cell {
    display: block; aspect-ratio: 1 / 1;
    background: var(--panel); border: 1px solid var(--border);
    border-radius: 6px; overflow: hidden;
    transition: border-color 0.15s;
  }
  .crop-cell:hover { border-color: var(--accent); }
  .crop-cell img { width: 100%; height: 100%; object-fit: contain; }
</style>
